import { stubForType } from './packageStubs';

const PRODUCED_AT = '2026-08-18T18:00:00Z';

function digest(text) {
  const src = String(text ?? '');
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i += 1) h = Math.imul(h ^ src.charCodeAt(i), 0x01000193);
  const hex = (h >>> 0).toString(16).padStart(8, '0');
  return `${hex}${hex}${hex}${hex}${hex}${hex}${hex}${hex}`.slice(0, 64);
}

function inlineHandle(producedBy, text, preview, mediaType = 'text/markdown') {
  const body = String(text ?? '');
  return {
    kind: 'agent.handle.v1',
    scheme: 'inline',
    media_type: mediaType,
    sha256: digest(body),
    bytes: body.length,
    preview: preview || (body.length > 80 ? `${body.slice(0, 79)}…` : body),
    truncated: false,
    text: body,
    produced_by: producedBy,
    produced_at: PRODUCED_AT,
  };
}

function usage(tokens = 2400) {
  return {
    model: 'grok-4.5',
    provider: 'xai',
    input_tokens: Math.max(100, tokens - 400),
    output_tokens: 400,
    total_tokens: tokens,
    truncated_input: false,
  };
}

function pkg(projectId, spec) {
  const stub = stubForType(spec.type);
  if (!stub) {
    throw new Error(`unknown template package type ${spec.type}`);
  }
  const inputs = spec.inputs || stub.inputs;
  return {
    id: spec.id,
    name: spec.name,
    type: spec.type,
    project_id: projectId,
    inputs,
    outputs: stub.outputs,
    parameters: stub.parameters,
    parameter_data: spec.parameter_data || {},
    deploy_status: spec.deploy_status || 'DEPLOYED',
    output_data: spec.output_data || {},
  };
}

function conn(projectId, id, source, target, sourceHandle, targetHandle) {
  return {
    id,
    project_id: projectId,
    source_package_id: source,
    target_package_id: target,
    source_handle: sourceHandle,
    target_handle: targetHandle,
  };
}

function thinkOut(id, text, preview) {
  return {
    thought: inlineHandle(id, text, preview),
    usage: usage(text.length + 800),
  };
}

/** Weekday 07:00 tick → 3 parallel branches (2 inference + 1 script) → join → digest. */
function scheduledFanout() {
  const id = 'tpl-schedule-fanout';
  const joinInputs = {
    type: 'object',
    required: ['branch_a', 'branch_b', 'branch_c'],
    properties: stubForType('workflow/join').inputs.properties,
  };
  return {
    id,
    name: 'Scheduled fan-out',
    summary: 'Timer fires on a cron. Several steps run in parallel. A join waits for every branch, then one consumer writes the digest.',
    patterns: ['timer', 'fan-out', 'join'],
    packages: [
      pkg(id, {
        id: `${id}-timer`,
        name: 'weekday-0700',
        type: 'workflow/timer',
        parameter_data: {
          cron: '0 7 * * 1-5',
          timezone: 'America/Los_Angeles',
          label: 'weekday morning',
        },
        output_data: {
          tick: inlineHandle(
            `${id}-timer`,
            'tick 2026-08-18T07:00:00-07:00 weekday morning',
            '0 7 * * 1-5',
            'text/plain'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-sentiment`,
        name: 'infer-sentiment',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Score overnight support tickets: positive / neutral / negative. Return counts only.',
          model: 'grok-4.5',
          role: 'general',
        },
        output_data: thinkOut(
          `${id}-sentiment`,
          'positive=41  neutral=12  negative=7',
          '41 / 12 / 7'
        ),
      }),
      pkg(id, {
        id: `${id}-topics`,
        name: 'infer-topics',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Cluster the same tickets into at most 5 topics. One line each.',
          model: 'grok-4.5',
          role: 'research',
        },
        output_data: thinkOut(
          `${id}-topics`,
          '- billing\n- login\n- shipping delay\n- feature ask\n- spam',
          '5 topics'
        ),
      }),
      pkg(id, {
        id: `${id}-metrics`,
        name: 'pull-metrics',
        type: 'workflow/script',
        parameter_data: {
          language: 'python',
          code: 'print("p95_ms=180\\nerror_rate=0.4")',
        },
        output_data: {
          result: inlineHandle(
            `${id}-metrics`,
            'p95_ms=180\nerror_rate=0.4',
            'p95 180ms',
            'text/plain'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-join`,
        name: 'wait-all',
        type: 'workflow/join',
        inputs: joinInputs,
        parameter_data: { reduce: 'json' },
        output_data: {
          combined: inlineHandle(
            `${id}-join`,
            '{"sentiment":"41/12/7","topics":5,"p95_ms":180}',
            '3 branches',
            'application/json'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-digest`,
        name: 'write-digest',
        type: 'agent/artifact',
        parameter_data: {
          sink: 'json',
          filename: 'morning-digest.json',
          media_type: 'application/json',
        },
        output_data: {
          handle: inlineHandle(
            `${id}-digest`,
            '{"sentiment":"41/12/7","topics":5,"p95_ms":180}',
            'morning-digest.json',
            'application/json'
          ),
        },
      }),
    ],
    connections: [
      conn(id, `${id}-c1`, `${id}-timer`, `${id}-sentiment`, 'tick', 'context'),
      conn(id, `${id}-c2`, `${id}-timer`, `${id}-topics`, 'tick', 'context'),
      conn(id, `${id}-c3`, `${id}-timer`, `${id}-metrics`, 'tick', 'context'),
      conn(id, `${id}-c4`, `${id}-sentiment`, `${id}-join`, 'thought', 'branch_a'),
      conn(id, `${id}-c5`, `${id}-topics`, `${id}-join`, 'thought', 'branch_b'),
      conn(id, `${id}-c6`, `${id}-metrics`, `${id}-join`, 'result', 'branch_c'),
      conn(id, `${id}-c7`, `${id}-join`, `${id}-digest`, 'combined', 'data'),
    ],
  };
}

/** Question in → two researchers in parallel → join → critic → brief. */
function researchDiamond() {
  const id = 'tpl-research-diamond';
  const joinInputs = {
    type: 'object',
    required: ['branch_a', 'branch_b'],
    properties: stubForType('workflow/join').inputs.properties,
  };
  return {
    id,
    name: 'Research diamond',
    summary: 'A question fans out to two researchers. A join waits for both, then a critic writes the brief.',
    patterns: ['fan-out', 'join', 'critic'],
    packages: [
      pkg(id, {
        id: `${id}-ask`,
        name: 'question',
        type: 'agent/input',
        parameter_data: {
          source: 'inline',
          text: 'Should we put agent think on Cloud Run or keep it in-process?',
          media_type: 'text/plain',
        },
        output_data: {
          handle: inlineHandle(
            `${id}-ask`,
            'Should we put agent think on Cloud Run or keep it in-process?',
            'Cloud Run vs in-process',
            'text/plain'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-ops`,
        name: 'researcher-ops',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Argue for in-process think. Ops cost, latency, local replay.',
          model: 'grok-4.5',
          role: 'research',
        },
        output_data: thinkOut(
          `${id}-ops`,
          'In-process: one binary, replay from fixtures, no extra IAM. Cold start is the tax on Cloud Run.',
          'in-process'
        ),
      }),
      pkg(id, {
        id: `${id}-scale`,
        name: 'researcher-scale',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Argue for Cloud Run think. Burst, isolation, per-tenant keys.',
          model: 'grok-4.5',
          role: 'research',
        },
        output_data: thinkOut(
          `${id}-scale`,
          'Cloud Run: isolate tenant keys, scale to zero between fleets, crash one think without taking the API.',
          'Cloud Run'
        ),
      }),
      pkg(id, {
        id: `${id}-join`,
        name: 'wait-both',
        type: 'workflow/join',
        inputs: joinInputs,
        parameter_data: { reduce: 'concat' },
        output_data: {
          combined: inlineHandle(
            `${id}-join`,
            'OPS: in-process for replay.\nSCALE: Cloud Run for isolation.',
            '2 briefs'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-critic`,
        name: 'critic',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Merge the two briefs. Recommend a default and one escape hatch.',
          model: 'grok-4.5',
          role: 'general',
        },
        output_data: thinkOut(
          `${id}-critic`,
          'Default in-process. Offer Cloud Run when a fleet sets isolate=true.',
          'in-process + isolate flag'
        ),
      }),
      pkg(id, {
        id: `${id}-brief`,
        name: 'write-brief',
        type: 'agent/artifact',
        parameter_data: {
          sink: 'file',
          filename: 'decision.md',
          media_type: 'text/markdown',
        },
        output_data: {
          handle: inlineHandle(
            `${id}-brief`,
            '# Decision\nDefault in-process. Cloud Run when isolate=true.',
            'decision.md'
          ),
        },
      }),
    ],
    connections: [
      conn(id, `${id}-c1`, `${id}-ask`, `${id}-ops`, 'handle', 'context'),
      conn(id, `${id}-c2`, `${id}-ask`, `${id}-scale`, 'handle', 'context'),
      conn(id, `${id}-c3`, `${id}-ops`, `${id}-join`, 'thought', 'branch_a'),
      conn(id, `${id}-c4`, `${id}-scale`, `${id}-join`, 'thought', 'branch_b'),
      conn(id, `${id}-c5`, `${id}-join`, `${id}-critic`, 'combined', 'context'),
      conn(id, `${id}-c6`, `${id}-critic`, `${id}-brief`, 'thought', 'data'),
    ],
  };
}

/** Nightly cron → gather script → two reviewers in parallel → join → notes. */
function nightlyReview() {
  const id = 'tpl-nightly-review';
  const joinInputs = {
    type: 'object',
    required: ['branch_a', 'branch_b'],
    properties: stubForType('workflow/join').inputs.properties,
  };
  return {
    id,
    name: 'Nightly review',
    summary: 'A 02:00 timer gathers the diff, then correctness and security review in parallel. Join writes the note after both finish.',
    patterns: ['timer', 'script', 'fan-out', 'join'],
    packages: [
      pkg(id, {
        id: `${id}-timer`,
        name: 'nightly-0200',
        type: 'workflow/timer',
        parameter_data: {
          cron: '0 2 * * *',
          timezone: 'America/Los_Angeles',
          label: 'nightly',
        },
        output_data: {
          tick: inlineHandle(
            `${id}-timer`,
            'tick 2026-08-18T02:00:00-07:00 nightly',
            '0 2 * * *',
            'text/plain'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-diff`,
        name: 'gather-diff',
        type: 'workflow/script',
        parameter_data: {
          language: 'bash',
          code: 'git log --oneline -20 && git diff origin/main...HEAD',
        },
        output_data: {
          result: inlineHandle(
            `${id}-diff`,
            '2d5e0a7 fix think context\n0fff3be cloud build trigger',
            '2 commits',
            'text/plain'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-correct`,
        name: 'review-correctness',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Review the diff for logic bugs. List file + issue or "none".',
          model: 'grok-4.5',
          role: 'coding',
        },
        output_data: thinkOut(
          `${id}-correct`,
          'none — think.go now passes ExecutionContext into consumeHandle.',
          'none'
        ),
      }),
      pkg(id, {
        id: `${id}-sec`,
        name: 'review-security',
        type: 'agent/think',
        parameter_data: {
          prompt: 'Review the same diff for secret leaks and SSRF. List or "none".',
          model: 'grok-4.5',
          role: 'qa',
        },
        output_data: thinkOut(
          `${id}-sec`,
          'none — no new outbound fetch in this range.',
          'none'
        ),
      }),
      pkg(id, {
        id: `${id}-join`,
        name: 'wait-reviews',
        type: 'workflow/join',
        inputs: joinInputs,
        parameter_data: { reduce: 'concat' },
        output_data: {
          combined: inlineHandle(
            `${id}-join`,
            'correctness: none\nsecurity: none',
            '2 reviews'
          ),
        },
      }),
      pkg(id, {
        id: `${id}-notes`,
        name: 'ship-notes',
        type: 'agent/sink',
        parameter_data: { note: 'nightly review clean' },
        output_data: {
          ok: true,
          note: 'nightly review clean',
          handle: inlineHandle(
            `${id}-notes`,
            'correctness: none\nsecurity: none',
            'clean'
          ),
        },
      }),
    ],
    connections: [
      conn(id, `${id}-c1`, `${id}-timer`, `${id}-diff`, 'tick', 'context'),
      conn(id, `${id}-c2`, `${id}-diff`, `${id}-correct`, 'result', 'context'),
      conn(id, `${id}-c3`, `${id}-diff`, `${id}-sec`, 'result', 'context'),
      conn(id, `${id}-c4`, `${id}-correct`, `${id}-join`, 'thought', 'branch_a'),
      conn(id, `${id}-c5`, `${id}-sec`, `${id}-join`, 'thought', 'branch_b'),
      conn(id, `${id}-c6`, `${id}-join`, `${id}-notes`, 'combined', 'data'),
    ],
  };
}

/** Undeployed bash-harness blocks so Play / Stop / Delete can be tested without an agent. */
function harnessLifecycle() {
  const id = 'tpl-harness-lifecycle';
  const joinInputs = {
    type: 'object',
    required: ['branch_a', 'branch_b'],
    properties: stubForType('workflow/join').inputs.properties,
  };
  const undeployed = (spec) =>
    pkg(id, { ...spec, deploy_status: 'NOT_DEPLOYED', output_data: {} });
  return {
    id,
    name: 'Harness provision/deprovision',
    summary:
      'Placeholder blocks on a bash harness. Play provisions upstream then the node. Stop deprovisions downstream then the node. Trash deletes downstream, then edges, then the card.',
    patterns: ['bash', 'placeholder', 'provision', 'deprovision'],
    packages: [
      undeployed({
        id: `${id}-tick`,
        name: 'start',
        type: 'workflow/timer',
        parameter_data: {
          cron: '* * * * *',
          timezone: 'America/Los_Angeles',
          label: 'harness tick',
        },
      }),
      undeployed({
        id: `${id}-left`,
        name: 'provision-left',
        type: 'workflow/placeholder',
        parameter_data: { name: 'left', note: 'fan-out A' },
      }),
      undeployed({
        id: `${id}-right`,
        name: 'provision-right',
        type: 'workflow/placeholder',
        parameter_data: { name: 'right', note: 'fan-out B' },
      }),
      undeployed({
        id: `${id}-join`,
        name: 'wait-both',
        type: 'workflow/join',
        inputs: joinInputs,
        parameter_data: { reduce: 'concat' },
      }),
      undeployed({
        id: `${id}-sink`,
        name: 'consume',
        type: 'workflow/placeholder',
        parameter_data: { name: 'consume', note: 'after join' },
      }),
    ],
    connections: [
      conn(id, `${id}-c1`, `${id}-tick`, `${id}-left`, 'tick', 'context'),
      conn(id, `${id}-c2`, `${id}-tick`, `${id}-right`, 'tick', 'context'),
      conn(id, `${id}-c3`, `${id}-left`, `${id}-join`, 'resource', 'branch_a'),
      conn(id, `${id}-c4`, `${id}-right`, `${id}-join`, 'resource', 'branch_b'),
      conn(id, `${id}-c5`, `${id}-join`, `${id}-sink`, 'combined', 'context'),
    ],
  };
}

export const WORKFLOW_TEMPLATES = [
  scheduledFanout(),
  researchDiamond(),
  nightlyReview(),
  harnessLifecycle(),
];

export const WORKFLOW_TEMPLATE_IDS = new Set(WORKFLOW_TEMPLATES.map((t) => t.id));

export function getWorkflowTemplate(projectId) {
  return WORKFLOW_TEMPLATES.find((t) => t.id === projectId) || null;
}
